import {
  Divider,
  Icon,
  IconName,
  Theme,
  Toasts,
  Tooltip,
} from '@infotech-soe/erp-ui-kit';
import color from 'color';
import React, { forwardRef, memo, Ref, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import { TOTALS_FORMULA_ATTACHMENT } from '../../../../models/user-config/DisplayModeConfig';
import { DatasetGridColumn } from '../../../../services/dataset/view-model/DatasetGridColumn';
import { useAppSelector } from '../../../../store/hooks';
import {
  getDatasetColumns,
  getDatasetComputedTotals,
  getDatasetConfigCurrentPreset,
  getDatasetRows,
  isTotalsInvalid,
} from '../../../../store/selectors/dataset';
import { getCurrentLocaleName } from '../../../../store/selectors/intl';
import { copyTextToClipboard } from '../utils/copyTextToClipboard';

import { formulasFormMessages } from './FormulasForm';

interface TotalsCellProps {
  datasetId: string;
  field: string;
  last?: boolean;
}

const iconMap = {
  [TOTALS_FORMULA_ATTACHMENT.ALL]: 'all',
  [TOTALS_FORMULA_ATTACHMENT.SELECTED]: 'selected',
} satisfies Record<TOTALS_FORMULA_ATTACHMENT, IconName>;

const messages = defineMessages({
  updateData: {
    id: 'core.category.grid.totals.updateData',
    defaultMessage: 'Update data (F5)',
  },
  copy: {
    id: 'core.errorNotification.copy',
    defaultMessage: 'Copy to clipboard',
  },
  copied: {
    id: 'core.errorNotification.copied',
    defaultMessage: 'Copied!',
  },
});

const useStyles = createUseStyles(({ colors }: Theme) => ({
  totalsCell: {
    composes: 'row no-wrap',
    flexShrink: 0,
  },
  value: {
    cursor: 'copy',
    userSelect: 'none',
    '&:hover': {
      color: color(colors.typoCommon).alpha(0.6).string(),
    },
  },
  warning: {
    color: colors.warning,
  },
  toast: {
    flex: 'auto',
  },
}));

const getColumnByField = (fieldName: string, columns: DatasetGridColumn[]) =>
  columns.find(column => column.field === fieldName);

const TotalsCell = (
  { datasetId, field, last }: TotalsCellProps,
  ref: Ref<HTMLDivElement>
): React.JSX.Element | null => {
  const classes = useStyles();
  const { formatMessage, formatNumber } = useIntl();
  const columns = useAppSelector(state => getDatasetColumns(state, datasetId));
  const currentLocale = useAppSelector(getCurrentLocaleName);
  const { rules, totals: totalsConfig } = useAppSelector(state =>
    getDatasetConfigCurrentPreset(state, datasetId)
  );
  const totals = useAppSelector(state =>
    getDatasetComputedTotals(state, datasetId)
  );
  const totalsInvalid = useAppSelector(state =>
    isTotalsInvalid(state, datasetId)
  );
  const firstRow = useAppSelector(state => getDatasetRows(state, datasetId)[0]);

  const { title, domain, formatter } = getColumnByField(field, columns) ?? {};

  const [isCopyToastOpen, setCopyToastOpen] = useState(false);

  const handleClick = async (total: number) => {
    await copyTextToClipboard(total.toString());
    setCopyToastOpen(true);
  };

  const headerLabel = useMemo(
    () => rules[field].title?.[currentLocale] || title || field,
    [rules, field, currentLocale, title]
  );

  const numberFormatOpts = useMemo(
    () =>
      domain?.precision !== undefined
        ? {
            minimumFractionDigits: domain.precision,
            maximumFractionDigits: domain.precision,
          }
        : undefined,
    [domain?.precision]
  );

  if (!totalsConfig || !totals) {
    return null;
  }

  return (
    <div className={classes.totalsCell} ref={ref}>
      {headerLabel}:&nbsp;
      {totalsConfig[field]?.map(([id, formula, attachment], index, arr) => (
        <div key={id} className="row no-wrap" role="listitem">
          <Tooltip label={formatMessage(formulasFormMessages[formula])}>
            <Icon name={formula} className="m-pl-sm" />
          </Tooltip>
          <Tooltip label={formatMessage(formulasFormMessages[attachment])}>
            <Icon name={iconMap[attachment]} className="m-pl-sm" />
          </Tooltip>
          <Tooltip label={formatMessage(messages.copy)}>
            <span
              role="button"
              tabIndex={0}
              className={`${classes.value} m-pl-sm`}
              onClick={() => handleClick(totals[id])}
              onKeyDown={e => e.key === 'Enter' && handleClick(totals[id])}
            >
              {formatter && totals[id] !== null && totals[id] !== undefined
                ? formatter(totals[id], { row: firstRow ?? {} })
                : formatNumber(totals[id], numberFormatOpts)}
            </span>
          </Tooltip>
          {attachment === TOTALS_FORMULA_ATTACHMENT.ALL && totalsInvalid && (
            <Tooltip label={formatMessage(messages.updateData)}>
              <Icon name="alert" className={`${classes.warning} m-pl-sm`} />
            </Tooltip>
          )}
          {last && index === arr.length - 1 ? null : (
            // eslint-disable-next-line react/no-array-index-key
            <Divider key={index} type="vertical" />
          )}
        </div>
      ))}
      <Toasts open={isCopyToastOpen}>
        <Toasts.Toast
          autoDismissTimeOut={1500}
          className={classes.toast}
          type="success"
          onClose={() => setCopyToastOpen(false)}
        >
          {formatMessage(messages.copied)}
        </Toasts.Toast>
      </Toasts>
    </div>
  );
};

export default memo(forwardRef(TotalsCell));
